import { ChevronDown, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchSyncStatus, postSyncAccounting } from '../../api/dashboard';

const healthClass = {
  green: 'bg-emerald-100 text-emerald-800',
  yellow: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};

export function AccountingSyncStatus({ workspace, onSynced }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const s = await fetchSyncStatus(workspace);
      setData(s);
    } catch (e) {
      setErr(e.message);
    }
  }, [workspace]);

  useEffect(() => {
    load();
  }, [load]);

  const onSyncNow = async () => {
    setSyncing(true);
    setErr(null);
    try {
      await postSyncAccounting(workspace, []);
      await load();
      onSynced?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const toolLabel = data?.tool === 'zoho' ? 'Zoho Books' : 'Tally';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium uppercase text-slate-500">Accounting sync</p>
          <p className="mt-1 font-semibold text-slate-900">Connected to {toolLabel}</p>
          {data?.lastSyncAt ? (
            <p className="mt-0.5 text-xs text-slate-600">
              Last sync: {new Date(data.lastSyncAt).toLocaleString('en-IN')}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-amber-700">No sync recorded yet — connect your tool</p>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${healthClass[data?.connectionHealth] || healthClass.red}`}
        >
          {data?.connectionHealth || 'unknown'}
        </span>
      </div>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      <button
        type="button"
        disabled={syncing}
        onClick={onSyncNow}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
      >
        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? 'Syncing…' : 'Sync now'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="mt-2 flex w-full items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
      >
        History <ChevronDown className={`h-3 w-3 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && data?.history?.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-xs text-slate-600">
          {data.history.map((h, i) => (
            <li key={i} className="flex justify-between gap-2">
              <span>{h.status}</span>
              <span className="font-mono text-[10px] text-slate-500">{h.synced_at}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
